// Platform-aware modifier key glyph + helpers.
//
// MASTER_PROMPT §1.3 (CRITICAL): every visible keyboard shortcut hint must
// flip the Apple Command symbol to the word "Ctrl" on Windows/Linux. The
// actual key binding already works cross-platform via useKeyboardShortcuts
// (`meta: true` matches metaKey OR ctrlKey), so this module is purely
// cosmetic — it returns the glyph that matches what the user actually
// presses on their platform.
//
// THIS FILE IS THE ONLY PLACE in the codebase that may contain the Apple
// Command glyph as a literal. A full-codebase grep for that symbol should
// return zero hits outside this file.

import { useState, useEffect } from "react";

// Module-level constants — both literals live ONLY here.
const MAC_MOD = "⌘"; // U+2318 = Place of Interest Sign. Never literal anywhere else.
const NON_MAC_MOD = "Ctrl";

const isClient = typeof window !== "undefined";

function detectIsMac(): boolean {
  if (!isClient) return true; // SSR default — most laptops in our target market are Mac
  // navigator.platform is deprecated but still the most reliable check for
  // Mac vs Windows/Linux. userAgentData is not yet supported in Safari.
  return /Mac|iPod|iPhone|iPad/.test(navigator.platform || "");
}

/** True when the user is on a Mac-family device. SSR-safe (defaults true). */
export function useIsMac(): boolean {
  const [isMac, setIsMac] = useState<boolean>(detectIsMac());
  useEffect(() => {
    setIsMac(detectIsMac());
  }, []);
  return isMac;
}

/** Returns the Command glyph on Mac, the word "Ctrl" on Windows/Linux. */
export function useModKey(): string {
  return useIsMac() ? MAC_MOD : NON_MAC_MOD;
}

/** Returns an aria-keyshortcuts string (e.g. "Meta+K" or "Control+K"). */
export function useAriaKeyshortcut(key: string): string {
  return `${useIsMac() ? "Meta" : "Control"}+${key}`;
}

/** Module-level constants for non-React callers. Computed once on first
 *  import; use the hooks in React components so the value updates on
 *  client hydration if SSR ever lands. */
export const IS_MAC = detectIsMac();
export const MOD_KEY = IS_MAC ? MAC_MOD : NON_MAC_MOD;
export const MOD_KEY_CODE: "metaKey" | "ctrlKey" = IS_MAC ? "metaKey" : "ctrlKey";
